import React, { useCallback, useMemo } from 'react';

import {
  StyledBody,
  StyledCheckboxSpan,
  StyledInput,
  StyledLabel,
  StyledSVGIcon,
  StyledWrapper,
} from './styles';
import ThemeV2 from '../../../componentsV2/theme';
import useToggle from '../../../hooks/useToggle';
import { mapHexCodeToEmoticon } from '../../../Utils/mappers';

interface CheckboxProps {
  label?: React.ReactNode;
  labelMargin?: string;
  labelPadding?: string;
  labelVariant?: string;
  name: string;
  value?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  size?: string;
  color?: string;
  className?: string;
  disabled?: boolean;
  stopClickPropagation?: boolean;
  icon?: string;
  classes?: unknown;
}

const Checkbox = (props: CheckboxProps) => {
  const {
    name,
    label,
    labelMargin = undefined,
    labelPadding = undefined,
    labelVariant = 'body2',
    value = false,
    size = '14px',
    color = ThemeV2.palette.geekBlue6,
    onChange,
    className,
    disabled = false,
    stopClickPropagation,
    icon,
  } = props;
  const {
    models: { toggleValue: isFocused },
    operations: { setToggleToFalse, setToggleToTrue },
  } = useToggle();

  const handleOnFocus = () => {
    setToggleToTrue();
  };
  const handleOnBlur = () => {
    setToggleToFalse();
  };

  const svgColor = useMemo(() => {
    if (disabled) {
      return ThemeV2.palette.gray3;
    }
    if (!value) {
      return ThemeV2.palette.gray6;
    }

    return color;
  }, [color, value, disabled]);

  const hoverColor = useMemo(() => {
    if (disabled) {
      return undefined;
    }
    return color;
  }, [color, disabled]);

  const onClick = useCallback(
    (e) => {
      if (stopClickPropagation) {
        e.stopPropagation();
      }
    },
    [stopClickPropagation],
  );

  return (
    <StyledWrapper className={className}>
      <StyledLabel htmlFor={name} checkboxSize={size} disabled={disabled}>
        <StyledCheckboxSpan onClick={onClick}>
          <StyledInput
            type="checkbox"
            value={label?.toString()}
            checked={value}
            id={name}
            name={name}
            onChange={onChange}
            disabled={disabled}
            onBlur={handleOnBlur}
            onKeyUp={handleOnFocus}
            data-testid="checkbox"
          />
          <StyledSVGIcon
            icon={value ? 'checkbox-checked' : 'checkbox-unchecked'}
            size={size}
            color={svgColor}
            hoverColor={hoverColor}
            $isFocused={isFocused}
          />
        </StyledCheckboxSpan>
        {icon && <StyledBody> {mapHexCodeToEmoticon(icon)} </StyledBody>}
        {label && (
          <StyledBody
            $margin={labelMargin}
            $padding={labelPadding}
            variant={labelVariant}
          >
            {label}
          </StyledBody>
        )}
      </StyledLabel>
    </StyledWrapper>
  );
};

export default Checkbox;
